/**
 * Shared workspace layout primitives for the center detail experience.
 *
 * These standardize the "compact record list + center detail pane" pattern
 * used by Tasks, Websites, and the managed tables so each page stops
 * re-implementing the same split grid, picker list, and detail scaffold.
 *
 * Composition map:
 *   <WorkspaceSplitView detail={...} footer={...}>     // split grid
 *     <CompactRecordList title="...">                  // picker list shell
 *       <CompactRecordListItem ... />                  // selectable row
 *     </CompactRecordList>
 *   </WorkspaceSplitView>
 *
 *   <RecordDetailShell title recordType onClose ...>   // section + nav header
 *     <RecordDetailHeading icon|media title badges actions /> // identity block
 *     ...body...
 *   </RecordDetailShell>
 *
 * The nav header (close / breadcrumb / prev-next) lives in RecordDetailShell
 * via the shared RecordDetailHeader. The identity block (avatar/icon, title,
 * status, primary actions) lives in RecordDetailHeading. Pages that need a
 * bespoke body header (e.g. Tasks) can skip RecordDetailHeading and render
 * their own block inside the shell.
 *
 * App-local for now (mirrors the management kit). Promote to
 * `@command-center/ui-system` once a second portal needs it.
 */
import { type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { RecordDetailHeader } from '@/components/record-detail-header';
import { cn } from '@/lib/utils';

export { RecordDetailHeader } from '@/components/record-detail-header';

/**
 * Split grid: a constrained compact list on the left and a flexible detail
 * pane on the right. Collapses to a single full-width column when no detail
 * is open (i.e. the index table/cards view).
 */
export function WorkspaceSplitView({
  children,
  detail,
  footer,
  className,
}: {
  /** The list / table / cards rendered in the left column. */
  children: ReactNode;
  /** The detail pane. When present the grid splits into two columns. */
  detail?: ReactNode;
  /** Optional footer summary rendered beneath the list column. */
  footer?: ReactNode;
  className?: string;
}) {
  const hasDetail = Boolean(detail);
  return (
    <div
      className={cn(
        'grid gap-4',
        hasDetail && 'xl:grid-cols-[minmax(260px,340px)_minmax(0,1fr)]',
        className,
      )}
    >
      <div className="min-w-0 space-y-3">
        {children}
        {footer}
      </div>
      {detail}
    </div>
  );
}

/**
 * Bordered, scrollable picker list shown in the left column while a record is
 * open. Pages keep their own empty-state handling and render this only when
 * there are rows.
 */
export function CompactRecordList({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('overflow-hidden rounded-lg border border-border bg-card', className)}>
      <div className="border-b border-border px-3 py-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          {title}
        </div>
      </div>
      <div className="max-h-[calc(100vh-310px)] overflow-y-auto p-1.5">{children}</div>
    </div>
  );
}

/**
 * A selectable row inside CompactRecordList. Slots render in a fixed order:
 * media (leading), title, badges, subtitle, meta — so pages with different
 * shapes (Tasks shows badges, Websites/managed tables show media+subtitle)
 * stay visually consistent.
 */
export function CompactRecordListItem({
  selected = false,
  onSelect,
  media,
  title,
  titleLines = 1,
  badges,
  subtitle,
  meta,
}: {
  selected?: boolean;
  onSelect: () => void;
  media?: ReactNode;
  title: string;
  titleLines?: 1 | 2;
  badges?: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors',
        selected ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted/70',
      )}
    >
      {media}
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block text-[13px] font-medium',
            titleLines === 2 ? 'line-clamp-2 leading-snug' : 'truncate',
          )}
        >
          {title}
        </span>
        {badges && <span className="mt-1 flex flex-wrap items-center gap-1.5">{badges}</span>}
        {subtitle && (
          <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">{subtitle}</span>
        )}
        {meta && (
          <span className="mt-1 flex min-w-0 flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
            {meta}
          </span>
        )}
      </span>
    </button>
  );
}

/** Quiet initial-letter tile used when a compact list row has no media. */
export function CompactListMediaFallback({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'flex flex-shrink-0 items-center justify-center rounded-md border border-border/70 bg-primary/10 text-[12px] font-semibold text-primary',
        className,
      )}
    >
      {label.trim().slice(0, 1).toUpperCase() || '?'}
    </span>
  );
}

/**
 * The center detail scaffold: a bordered section topped by the shared nav
 * header (close / breadcrumb / previous-next). Body content — including any
 * identity block — is passed as children.
 */
export function RecordDetailShell({
  title,
  recordType,
  backLabel,
  onClose,
  canGoPrevious,
  canGoNext,
  onPrevious,
  onNext,
  isEditing,
  onEdit,
  onSave,
  savePending,
  onDelete,
  deletePending,
  editLabel,
  deleteLabel,
  className,
  children,
}: {
  title: string;
  recordType?: string;
  backLabel?: string;
  onClose: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  /** Record actions shown in the header, right of the previous/next controls. */
  isEditing?: boolean;
  onEdit?: () => void;
  onSave?: () => void;
  savePending?: boolean;
  onDelete?: () => void;
  deletePending?: boolean;
  editLabel?: string;
  deleteLabel?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn('overflow-hidden rounded-lg border border-border bg-card', className)}>
      <RecordDetailHeader
        title={title}
        recordType={recordType}
        backLabel={backLabel}
        onClose={onClose}
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
        onPrevious={onPrevious}
        onNext={onNext}
        isEditing={isEditing}
        onEdit={onEdit}
        onSave={onSave}
        savePending={savePending}
        onDelete={onDelete}
        deletePending={deletePending}
        editLabel={editLabel}
        deleteLabel={deleteLabel}
      />
      {children}
    </section>
  );
}

/**
 * Full-width detail scaffold for master records that need a page-like
 * "homebase" instead of the compact split detail pane.
 */
export function RecordHomebaseShell({
  title,
  recordType,
  backLabel,
  onClose,
  canGoPrevious,
  canGoNext,
  onPrevious,
  onNext,
  actions,
  className,
  children,
}: {
  title: string;
  recordType?: string;
  backLabel?: string;
  onClose: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn('overflow-hidden rounded-lg border border-border bg-card', className)}>
      <RecordDetailHeader
        title={title}
        recordType={recordType}
        backLabel={backLabel}
        onClose={onClose}
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
        onPrevious={onPrevious}
        onNext={onNext}
      />
      {actions && <div className="border-b border-border px-4 py-3">{actions}</div>}
      <div className="bg-muted/20 p-4 md:p-5">{children}</div>
    </section>
  );
}

export function RelatedRecordTabs({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('rounded-lg border border-border bg-card p-3', className)}>{children}</div>;
}

/**
 * Standardized identity block for a detail pane: leading media (or icon),
 * record title, status/metadata badges, and optional primary actions.
 */
export function RecordDetailHeading({
  title,
  icon: Icon,
  media,
  badges,
  actions,
  className,
}: {
  title: ReactNode;
  icon?: LucideIcon;
  media?: ReactNode;
  badges?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 border-b border-border px-4 py-3 lg:flex-row lg:items-start',
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {media ??
          (Icon && (
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-5 w-5" aria-hidden />
            </div>
          ))}
        <div className="min-w-0 flex-1">
          <h2 className="break-words text-[15px] font-semibold leading-tight">{title}</h2>
          {badges && (
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
              {badges}
            </div>
          )}
        </div>
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}
